import {
  getRenderingEngine,
  eventTarget,
  utilities as csUtils,
  Enums as coreEnums,
} from "@cornerstonejs/core";
import { calibrateImageSpacing } from "@cornerstonejs/tools/utilities";
import { annotation as annotationModule } from "@cornerstonejs/tools";
import { getEnv } from "../../../helpers/EnvHelper";

const RENDERING_ENGINE_ID = getEnv("REACT_APP_RENDERING_ENGINE_ID");

// Tool name used for the calibration draw (see CalibrationLineTool).
export const CALIBRATION_TOOL_NAME = "CalibrationLine";

// Fired when a calibration line has been drawn — the CalibrationModal listens
// for this and opens to ask for the real distance. detail: { annotationUID,
// imageId, worldDistance }.
export const CALIBRATION_REQUEST_EVENT = "AUG_CALIBRATION_REQUEST";

// Fired after a calibration is applied so the "CAL: 1 mm = X px" badge re-reads
// the new scale. The native IMAGE_SPACING_CALIBRATED event is dispatched on the
// viewport element (not the global target), so we emit our own global signal.
export const CALIBRATION_APPLIED_EVENT = "AUG_CALIBRATION_APPLIED";

const dispatch = (type, detail) => {
  try {
    eventTarget.dispatchEvent(new CustomEvent(type, { detail }));
  } catch (_) {
    /* ignore */
  }
};

/** Remove a (transient) calibration annotation by UID and re-render. */
export const removeCalibrationLine = (annotationUID) => {
  try {
    annotationModule.state.removeAnnotation(annotationUID);
    const re = getRenderingEngine(RENDERING_ENGINE_ID);
    re?.render?.();
  } catch (_) {
    /* ignore */
  }
};

/**
 * Read back the user calibration ratio for an image, if any.
 * Returns the pixels-per-mm scale (so the badge can show "1 mm = scale px"),
 * or null when the image has no user calibration.
 */
export const getUserCalibrationScale = (imageId) => {
  try {
    if (!imageId) return null;
    const cal = csUtils.calibratedPixelSpacingMetadataProvider.get(
      "calibratedPixelSpacing",
      imageId
    );
    if (cal && cal.type === coreEnums.CalibrationTypes.USER && cal.scale > 0) {
      return cal.scale;
    }
  } catch (_) {
    /* ignore */
  }
  return null;
};

/**
 * Called on ANNOTATION_COMPLETED for a CalibrationLine draw. Computes the drawn
 * length (world space — equals the pixel length on an uncalibrated image) and
 * fires CALIBRATION_REQUEST_EVENT so the modal can prompt for the real mm value.
 *
 * NOTE: ANNOTATION_COMPLETED's detail only has `{ annotation }` (no viewportId),
 * so the image is taken from annotation.metadata.referencedImageId.
 */
export const requestCalibration = (annotation) => {
  try {
    const points = annotation?.data?.handles?.points;
    const imageId = annotation?.metadata?.referencedImageId;
    const annotationUID = annotation?.annotationUID;

    if (!imageId || !points || points.length < 2) {
      removeCalibrationLine(annotationUID);
      return;
    }

    const [w0, w1] = points;
    const worldDistance = Math.hypot(
      w1[0] - w0[0],
      w1[1] - w0[1],
      w1[2] - w0[2]
    );

    if (!isFinite(worldDistance) || worldDistance <= 0) {
      removeCalibrationLine(annotationUID);
      return;
    }

    dispatch(CALIBRATION_REQUEST_EVENT, {
      annotationUID,
      imageId,
      worldDistance,
    });
  } catch (e) {
    console.warn("Calibration request failed", e);
    removeCalibrationLine(annotation?.annotationUID);
  }
};

/**
 * Apply a user calibration computed from the drawn line.
 *
 * Cornerstone computes every length as worldDistance / scale, so setting
 * scale = worldDistance / realMm makes the drawn line read exactly realMm and
 * flips the unit from "px" to "mm" (calibrateImageSpacing stores a USER
 * calibration with scale > 0 → hasPixelSpacing becomes true). On an
 * uncalibrated image worldDistance == pixel length, so the stored scale is the
 * pixels-per-mm ratio the badge shows as "1 mm = scale px".
 *
 * @returns true on success.
 */
export const applyCalibration = (imageId, worldDistance, realMm) => {
  try {
    const renderingEngine = getRenderingEngine(RENDERING_ENGINE_ID);
    if (!renderingEngine || !imageId) return false;

    const mm = parseFloat(realMm);
    if (!isFinite(mm) || mm <= 0) return false;
    if (!isFinite(worldDistance) || worldDistance <= 0) return false;

    const scale = worldDistance / mm;
    if (!isFinite(scale) || scale <= 0) return false;

    // A numeric arg is treated as { type: USER, scale } by calibrateImageSpacing.
    calibrateImageSpacing(imageId, renderingEngine, scale);
    // Only loaded viewports — render() would blit stale off-screen content
    // onto empty viewports (see _renderLoadedViewports in CornerstoneViewportService).
    const idsToRender = renderingEngine
      .getViewports()
      .filter((vp) => vp.id.startsWith("thumbnail") || vp.getCurrentImageId?.() !== undefined)
      .map((vp) => vp.id);
    if (idsToRender.length > 0) {
      renderingEngine.renderViewports(idsToRender);
    }

    dispatch(CALIBRATION_APPLIED_EVENT, { imageId, scale });
    return true;
  } catch (e) {
    console.warn("Calibration apply failed", e);
    return false;
  }
};
